import MainGrid from "@/components/grid/MainGrid";
import LoadingDots from "@/components/LoadingDots";
import httpClient from "@/config/axios";
import { useIsAdmin, useLoginUser, useProfileImageUrl } from "@/hooks/useAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import UserForm from "./components/UserForm";
import ErrorComponent from "@/components/ui/error";
import { useState, useEffect, useRef } from "react";
import { images } from "@img/imagesMap";
import { Camera, X } from "lucide-react";
import { toast } from "@/components/toast/CustomToast";
import useStore from "@/store/store";
import { usePersonnelBasicInfo } from "@/hooks/usePersonnelBasicInfo";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import CustomModal from "@/components/modal/CustomModal";

export default function MyPage() {
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();
  const {
    data: userDetailData,
    isLoading,
    isError,
    error,
  } = usePersonnelBasicInfo();

  // localStorage에서 이모지 가져오기
  const [selectedEmoji, setSelectedEmoji] = useState<string>(() => {
    const savedEmoji = localStorage.getItem("selectedEmoji");
    return savedEmoji || images.icon_emoji_1;
  });
  const [isEmojiOpen, setIsEmojiOpen] = useState<boolean>(false);

  // 이모지 변경 시 localStorage에 저장
  useEffect(() => {
    localStorage.setItem("selectedEmoji", selectedEmoji);
  }, [selectedEmoji]);

  // 프로필 이미지 업로드 관련
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { setPersonnelBasicInfo } = useStore();

  // 프로필 이미지 삭제 Mutation
  const { mutate: deleteProfileImage, isPending: isDeleting } = useMutation({
    mutationFn: async () => {
      return await httpClient.delete("/nurses/profile-image");
    },
    onSuccess: async () => {
      // 전역 스토어에서 현재 gender 가져오기
      const currentGender = useStore.getState().gender;
      // profile_image_url을 null로 설정
      setPersonnelBasicInfo(null, currentGender);

      // 삭제 성공 시 프로필 정보 리프레시
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.personnelBasicInfo(),
      });

      // previewImage 초기화
      setPreviewImage(null);

      toast.success("프로필 이미지가 삭제되었습니다.");
    },
    onError: (error: unknown) => {
      console.error("프로필 이미지 삭제 실패:", error);
      let errorMessage = "프로필 이미지 삭제에 실패했습니다.";

      if (error && typeof error === "object") {
        if ("response" in error) {
          const axiosError = error as {
            response?: { data?: { detail?: string } };
          };
          errorMessage = axiosError.response?.data?.detail || errorMessage;
        } else if ("message" in error) {
          errorMessage = (error as { message: string }).message;
        }
      }

      toast.error(errorMessage);
    },
  });

  // 프로필 이미지 삭제 핸들러
  const handleDeleteImage = () => {
    if (!confirm("프로필 이미지를 삭제하시겠습니까?")) {
      return;
    }
    deleteProfileImage();
  };

  // 프로필 이미지 업로드 Mutation
  const { mutate: uploadProfileImage, isPending: isUploading } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return await httpClient.post("/nurses/profile-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });
    },
    onSuccess: async (response) => {
      // API 응답에서 profile_image_url 받아서 전역 스토어에 저장
      const responseData = response as {
        nurse_id?: string;
        profile_image_key?: string;
        profile_image_url?: string;
        profile_image_updated_at?: string;
      };

      if (responseData.profile_image_url) {
        const currentGender = useStore.getState().gender;
        setPersonnelBasicInfo(responseData.profile_image_url, currentGender);
      }

      // 업로드 성공 시 프로필 정보 리프레시
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.personnelBasicInfo(),
      });

      // previewImage 초기화 (서버 이미지로 교체됨)
      setPreviewImage(null);

      toast.success("프로필 이미지가 업로드되었습니다.");
    },
    onError: (error: unknown) => {
      console.error("프로필 이미지 업로드 실패:", error);
      let errorMessage = "프로필 이미지 업로드에 실패했습니다.";

      if (error && typeof error === "object") {
        if ("response" in error) {
          const axiosError = error as {
            response?: { data?: { detail?: string } };
          };
          errorMessage = axiosError.response?.data?.detail || errorMessage;
        } else if ("message" in error) {
          errorMessage = (error as { message: string }).message;
        }
      }

      toast.error(errorMessage);
    },
  });

  // 이미지 파일 유효성 검사 함수
  const validateImageFile = async (
    file: File
  ): Promise<{ valid: boolean; error?: string }> => {
    // 1. 파일 존재 확인
    if (!file) {
      return { valid: false, error: "파일이 선택되지 않았습니다." };
    }

    // 2. 파일 크기 제한 (3MB)
    const maxSize = 3 * 1024 * 1024; // 3MB
    if (file.size > maxSize) {
      return { valid: false, error: "파일 크기는 3MB를 초과할 수 없습니다." };
    }

    // 3. 파일 확장자 검사
    const validExtensions = [".jpg", ".jpeg", ".png", ".webp"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));
    if (!validExtensions.includes(fileExtension)) {
      return {
        valid: false,
        error: "지원되는 이미지 형식이 아닙니다. (PNG, JPG, JPEG, WEBP만 지원)",
      };
    }

    // 4. MIME 타입 검사
    const validImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];
    if (!validImageTypes.includes(file.type)) {
      return {
        valid: false,
        error: "지원되는 이미지 형식이 아닙니다. (PNG, JPG, JPEG, WEBP만 지원)",
      };
    }

    // 5. 실제 이미지 파일인지 검증 (Image 객체로 로드 테스트)
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // 이미지가 정상적으로 로드되면 유효한 이미지 파일
          resolve({ valid: true });
        };
        img.onerror = () => {
          // 이미지 로드 실패 시 유효하지 않은 파일
          resolve({
            valid: false,
            error:
              "유효한 이미지 파일이 아닙니다. 파일이 손상되었거나 이미지 형식이 올바르지 않습니다.",
          });
        };
        if (e.target?.result) {
          img.src = e.target.result as string;
        } else {
          resolve({
            valid: false,
            error: "파일을 읽을 수 없습니다.",
          });
        }
      };
      reader.onerror = () => {
        resolve({
          valid: false,
          error: "파일을 읽는 중 오류가 발생했습니다.",
        });
      };
      reader.readAsDataURL(file);
    });
  };

  // 파일 선택 핸들러
  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      // input 초기화 (같은 파일을 다시 선택할 수 있도록)
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // 파일 유효성 검사
    const validation = await validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error || "파일 유효성 검사에 실패했습니다.");
      // input 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // 미리보기 생성
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    // 업로드 실행
    uploadProfileImage(file);

    // input 초기화 (같은 파일을 다시 선택할 수 있도록)
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 카메라 버튼 클릭 핸들러
  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  // 프로필 이미지 소스 결정 (미리보기 > 전역 스토어 이미지)
  const globalProfileImageUrl = useProfileImageUrl();
  const profileImageSrc = previewImage || globalProfileImageUrl;

  // 전역 스토어에서 실제 업로드된 이미지 URL 확인 (기본 이미지가 아닌 실제 업로드된 이미지)
  const profileImageUrlFromStore = useStore((state) => state.profileImageUrl);
  const hasCustomImage = !!profileImageUrlFromStore && !previewImage;

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <LoadingDots />
      </div>
    );
  }

  if (isError) {
    const message =
      error instanceof Error
        ? error.message
        : "알 수 없는 오류가 발생했습니다.";
    return <ErrorComponent errorMsg={message} />;
  }

  const emojis = [
    images.icon_emoji_1,
    images.icon_emoji_2,
    images.icon_emoji_3,
    images.icon_emoji_4,
    images.icon_emoji_5,
    images.icon_emoji_6,
  ];

  return (
    <div className="w_main p-0">
      <MainGrid className="min-w-[1100px] max-w-[1300px]  p-4!">
        <h1 className="font-pretendard text-2xl font-bold text-sig mb-6 xl:mb-4">
          {isAdmin ? "계정관리" : "마이페이지"}
        </h1>

        {/* PC: 통합 카드 */}
        <div className="flex gap-8 bg-white rounded-[10px] p-10 py-25 shadow-[0_0_10px_0_rgba(0,0,0,0.15)]">
          {/* 프로필 섹션 */}
          <div className="px-[25px] w-full max-w-xs shrink-0">
            {/* Profile Image Section */}
            <div className="flex flex-col items-center">
              {/* 프로필 이미지 컨테이너 */}
              <div className="relative shrink-0 border-15 rounded-full border-zinc-100">
                {/* 숨김 파일 input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFileSelect}
                />

                {/* 프로필 이미지 */}
                <div className="relative">
                  <img
                    src={profileImageSrc}
                    alt="profile"
                    className="w-[142px] h-[142px] object-cover rounded-full"
                  />
                </div>

                {/* 이미지 삭제 버튼 - 카메라 위 (이미지가 있을 때만 표시) */}
                {hasCustomImage && (
                  <button
                    type="button"
                    className="absolute bottom-[44px] w-[36px] h-[36px] group bg-white rounded-full flex items-center justify-center shadow-[0_2px_8px_0_rgba(0,0,0,0.15)] hover:shadow-[0_4px_12px_0_rgba(0,0,0,0.2)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50"
                    style={{ right: "-24px" }}
                    onClick={handleDeleteImage}
                    disabled={isDeleting || isUploading}
                    title="프로필 이미지 삭제"
                  >
                    <X className="w-[20px] h-[20px] text-zinc-300 group-hover:text-red-500 transition-colors" />
                  </button>
                )}

                {/* 카메라 아이콘 배지 - 우측 하단 */}
                <button
                  type="button"
                  className="absolute bottom-0 right-0 w-[36px] h-[36px] group bg-white rounded-full flex items-center justify-center shadow-[0_2px_8px_0_rgba(0,0,0,0.15)] hover:shadow-[0_4px_12px_0_rgba(0,0,0,0.2)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleCameraClick}
                  disabled={isUploading || isDeleting}
                >
                  <Camera className="w-[20px] h-[20px] text-zinc-300 group-hover:text-zinc-500 transition-colors" />
                </button>
              </div>

              {/* PC: 이미지 아래 정보 */}
              <div className="mt-6">
                <div className="relative flex items-center justify-center gap-2 mb-[10px]">
                  <h2 className="text-2xl font-pretendard font-bold text-text-main2">
                    {loginUser?.name ?? "-"}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setIsEmojiOpen(!isEmojiOpen)}
                  >
                    <img
                      src={selectedEmoji}
                      alt="selected-emoji"
                      className="w-[20px] h-[20px]"
                    />
                  </button>

                  {/* 이모지 - PC만, 이름 위에 배치 */}
                  {isEmojiOpen && (
                    <div className="flex absolute bottom-full left-1/2 -translate-x-1/2 mb-2 gap-2 border p-[5px] w-max rounded-full shadow-[0_0_10px_0_rgba(0,0,0,0.15)] bg-white pointer-events-auto z-10">
                      {emojis.map((emoji, index) => (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEmoji(emoji);
                            setIsEmojiOpen(false);
                          }}
                          className="transition-transform hover:scale-110 cursor-pointer"
                        >
                          <img
                            src={emoji}
                            alt={`emoji-${index}`}
                            className="w-[20px] h-[20px] "
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-center space-y-1 mt-5">
                  <p className="font-pretendard text-text-main2 text-[18px]">
                    {loginUser?.office_name ?? "-"}
                  </p>
                  <p className="font-pretendard text-text-main2 text-[18px]">
                    {loginUser?.mb_part_name ?? "-"} {userDetailData?.role}
                    {/* {userDetailData?.work_place} */}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 유저정보 Form */}
          <div className="flex-1 w-full">
            <UserForm
              isAdmin={isAdmin}
              joining_date={userDetailData?.joining_date ?? ""}
              experience={userDetailData?.experience ?? 0}
              phone_number={userDetailData?.phone_number ?? ""}
              email={userDetailData?.email ?? ""}
            />
          </div>
        </div>
      </MainGrid>

      {/* 이미지 업로드/삭제 로딩 모달 */}
      <CustomModal.Confirm
        isOpen={isUploading || isDeleting}
        onClose={() => {}}
        isPending={isUploading || isDeleting}
        pendingMsg={
          isUploading
            ? "프로필 이미지를 업로드하는 중입니다..."
            : "프로필 이미지를 삭제하는 중입니다..."
        }
      />
    </div>
  );
}
